import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActionIconDef, Grid, GridActionIconClickEvent, GridConfig, GridRowCommitEvent, GridToolbarActionClickEvent } from '@inavista/components/grid';
import { AlertMessages } from 'projects/sales/src/app/shared/constants/app.constants';
import { SharedService } from 'projects/shared/src/lib/shared.service';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import {
  ADD_OTHER_ACTION, DELETE_OTHER_ACTION, OTHER_VACATIONS_COLUMNS, otherVacationsActionIcons, otherVacationsGridConfig,
} from './other-vacations-popup.grid.config';

const MAX_TOTAL_DAYS = 365;

// The request popup's "Other" days. The rows are the request's own list (data.popupData.other): edited, added and removed
// in place, as the Syncfusion grid did, and handed back on Add; Close without Add puts the list back as it was.
@Component({
    selector: 'app-other-vacations-popup',
    templateUrl: './other-vacations-popup.component.html',
    styleUrls: ['./other-vacations-popup.component.scss'],
    // The grid reads data.popupData.other, not an endpoint.
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OtherVacationsPopupComponent implements OnInit {
  @ViewChild('otherVacationsGrid') grid?: Grid;

  private readonly gridRows = inject(LocalRowsGridApi);

  canEdit = false;
  gridConfig!: GridConfig;
  actionIcons: ActionIconDef[] = [];
  readonly hasUnsavedChanges = signal(false);
  private originalStateOtherDaysArray: any[] = [];
  private lastKey = 0;
  private isEditing = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<OtherVacationsPopupComponent>,
    private sharedService: SharedService,
  ) {}

  ngOnInit() {
    this.canEdit = this.data?.mode === 'canEdit';
    this.data.popupData.other = this.data?.popupData?.other ?? [];
    this.originalStateOtherDaysArray = JSON.parse(JSON.stringify(this.data.popupData.other));
    for (const item of this.data.popupData.other) item.uId = ++this.lastKey;
    this.gridConfig = otherVacationsGridConfig(this.canEdit);
    this.actionIcons = otherVacationsActionIcons(this.canEdit);
    this.gridRows.columns.set(LocalRowsGridApi.schema(OTHER_VACATIONS_COLUMNS));
    this.refreshRows();
  }

  private get others(): any[] {
    return this.data.popupData.other;
  }

  private refreshRows() {
    this.gridRows.rows.set(this.others.map(item => ({ ...item })));
    this.grid?.refresh({ keepScroll: true });
  }

  getTotalOtherVacationDays(): number {
    return this.others.reduce((total: number, row: any) => total + Number(row?.days || 0), 0);
  }

  onRowEditStart() {
    this.isEditing = true;
  }

  onRowRevert() {
    this.isEditing = false;
  }

  // A changed line goes into the request's list, unless the other days would then pass 365 in all (as before).
  onRowCommit(commit: GridRowCommitEvent) {
    this.isEditing = false;
    const item = this.others.find(o => o.uId === commit.row['uId']);
    if (!item) return;
    const days = Math.round(Number(commit.row['days']));
    if (this.getTotalOtherVacationDays() - Number(item.days || 0) + days > MAX_TOTAL_DAYS) {
      this.sharedService.showNotification('info', 'You cannot add other vacations accumulatively more than 365!');
      this.refreshRows();
      return;
    }
    item.category = commit.row['category'] ?? item.category;
    item.days = days;
    this.hasUnsavedChanges.set(true);
    this.refreshRows();
  }

  // Add: a new line, "Other" for 1 day, open for typing (as before).
  onToolbarAction(event: GridToolbarActionClickEvent) {
    if (event.key !== ADD_OTHER_ACTION || !this.canEdit) return;
    this.endEdit();
    if (this.getTotalOtherVacationDays() + 1 > MAX_TOTAL_DAYS) {
      this.sharedService.showNotification('info', 'You cannot add other vacations accumulatively more than 365!');
      return;
    }
    const line = { uId: ++this.lastKey, id: 0, vrfId: null, category: 'Other', days: 1 };
    this.others.push(line);
    this.hasUnsavedChanges.set(true);
    this.refreshRows();
    setTimeout(() => this.grid?.startEdit(line.uId, { column: 'category' }));
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey !== DELETE_OTHER_ACTION || !this.canEdit) return;
    const row: any = event.row;
    this.sharedService.showConfirmation(`Are you sure to delete ${row?.days} days of vacation${row?.category ? ': ' + row.category : ''}?`)
      .then((res: any) => {
        if (!res?.isConfirmed) return;
        this.endEdit();
        const i = this.others.findIndex(o => o.uId === row.uId);
        if (i > -1) this.others.splice(i, 1);
        this.hasUnsavedChanges.set(true);
        this.refreshRows();
      });
  }

  // The open line is saved first (the old endEdit), so Add and Close see what was typed.
  private endEdit() {
    if (!this.isEditing) return;
    try { this.grid?.saveBatch(); } finally { this.isEditing = false; }
  }

  close(add?: boolean) {
    this.endEdit();
    const hasInvalidDays = this.others.some((row: any) => !row?.days || row?.days <= 0);
    if (add && hasInvalidDays) {
      this.sharedService.showNotification('info', 'Cannot add a record with zero or empty days!');
      return;
    }
    if (add && this.getTotalOtherVacationDays() > MAX_TOTAL_DAYS) {
      this.sharedService.showNotification('info', 'Total duration cannot be greater than 365 days!');
      return;
    }
    if (!add && this.hasUnsavedChanges()) {
      if (hasInvalidDays) {
        this.sharedService.showNotification('info', 'Cannot add a record with zero days!');
      } else {
        this.sharedService.showConfirmation(AlertMessages.unsavedChangesLost).then((res) => {
          if (res?.isConfirmed) {
            this.data.popupData.other = this.originalStateOtherDaysArray;
            this.returnOthers(false);
          }
        });
      }
    } else {
      this.returnOthers(this.hasUnsavedChanges());
    }
  }

  // The list as the request saves it: the grid's key dropped, the category trimmed ("Other" when empty).
  private returnOthers(interacted: boolean) {
    this.data.popupData.other = this.others.map(({ uId, ...d }: any) => ({ ...d, category: String(d?.category ?? 'Other').trim() }));
    this.dialogRef.close({ isInteracted: interacted, result: this.data.popupData.other });
  }
}
